// src/pages/RagAnalyticsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  ragListCollections,
  ragAnalyticsOverview,
} from "../api/client";

import "../styles/rag.css";
import "../styles/base.css";

// Charts – ensure: npm install recharts
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const DOC_STATUS_COLORS = {
  indexed: "#22c55e",
  unindexed: "#3b82f6",
  error: "#ef4444",
};

const QUERY_STATUS_COLORS = {
  success: "#22c55e",
  error: "#ef4444",
};

const JOB_STATUS_COLORS = {
  completed: "#22c55e",
  running: "#eab308",
  queued: "#38bdf8",
  error: "#ef4444",
};

export default function RagAnalyticsPage() {
  const [kbRows, setKbRows] = useState([]); // [{...collection, metrics:{...}}]
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");

  const [selectedId, setSelectedId] = useState("");

  // -----------------------------------
  // Load all KBs + per-KB overview
  // -----------------------------------
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErrMsg("");

        const collections = await ragListCollections(); // all KBs

        const metricsArr = await Promise.all(
          (collections || []).map((c) =>
            ragAnalyticsOverview(c.id).catch((e) => {
              console.error("Overview failed for KB", c.id, e);
              return null;
            })
          )
        );

        const merged = (collections || []).map((c, idx) => ({
          ...c,
          metrics: metricsArr[idx] || {},
        }));

        setKbRows(merged);
        if (merged.length > 0) {
          setSelectedId(String(merged[0].id));
        }
      } catch (err) {
        console.error("Failed to load analytics", err);
        setErrMsg("Failed to load RAG analytics.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const selectedRow = useMemo(
    () => kbRows.find((r) => String(r.id) === String(selectedId)),
    [kbRows, selectedId]
  );

  // -----------------------------------
  // GLOBAL AGGREGATES (all KBs)
  // -----------------------------------
  const globalAgg = useMemo(() => {
    let totalKb = kbRows.length;
    let totalDocs = 0;
    let totalChunks = 0;
    let totalQueries = 0;
    let sumLatency = 0;
    let latencyCount = 0;

    let querySuccess = 0;
    let queryError = 0;

    let jobsTotal = 0;
    let jobsCompleted = 0;
    let jobsRunning = 0;
    let jobsQueued = 0;
    let jobsError = 0;

    for (const r of kbRows) {
      const m = r.metrics || {};

      totalDocs += m.total_docs ?? 0;
      totalChunks += m.total_chunks ?? 0;
      totalQueries += m.query_count ?? 0;

      querySuccess += m.query_success ?? 0;
      queryError += m.query_error ?? 0;

      jobsTotal += m.jobs_total ?? 0;
      jobsCompleted += m.jobs_completed ?? 0;
      jobsRunning += m.jobs_running ?? 0;
      jobsQueued += m.jobs_queued ?? 0;
      jobsError += m.jobs_error ?? 0;

      if (m.avg_latency_ms != null && !Number.isNaN(m.avg_latency_ms)) {
        sumLatency += m.avg_latency_ms;
        latencyCount += 1;
      }
    }

    const avgLatency =
      latencyCount > 0 ? Math.round(sumLatency / latencyCount) : 0;

    return {
      totalKb,
      totalDocs,
      totalChunks,
      totalQueries,
      avgLatency,
      querySuccess,
      queryError,
      jobsTotal,
      jobsCompleted,
      jobsRunning,
      jobsQueued,
      jobsError,
    };
  }, [kbRows]);

  // -----------------------------------
  // GLOBAL CHART DATA
  // -----------------------------------
  const kbDocBarData = useMemo(
    () =>
      kbRows.map((r) => {
        const m = r.metrics || {};
        const totalDocs = m.total_docs ?? 0;
        const docsIndexed = m.docs_indexed ?? 0;
        const docsError = m.docs_error ?? 0;
        const docsUnindexed = Math.max(
          totalDocs - docsIndexed - docsError,
          0
        );
        return {
          name: `${r.id}: ${r.name}`,
          total: totalDocs,
          indexed: docsIndexed,
          unindexed: docsUnindexed,
          error: docsError,
        };
      }),
    [kbRows]
  );

  const kbChunkBarData = useMemo(
    () =>
      kbRows.map((r) => {
        const m = r.metrics || {};
        return {
          name: `${r.id}: ${r.name}`,
          chunks: m.total_chunks ?? 0,
        };
      }),
    [kbRows]
  );

  const queryStatusPieData = useMemo(
    () => [
      {
        name: "Success",
        key: "success",
        value: globalAgg.querySuccess,
      },
      {
        name: "Error",
        key: "error",
        value: globalAgg.queryError,
      },
    ].filter((d) => d.value > 0),
    [globalAgg]
  );

  const jobStatusPieData = useMemo(
    () => [
      {
        name: "Completed",
        key: "completed",
        value: globalAgg.jobsCompleted,
      },
      {
        name: "Running",
        key: "running",
        value: globalAgg.jobsRunning,
      },
      {
        name: "Queued",
        key: "queued",
        value: globalAgg.jobsQueued,
      },
      {
        name: "Error",
        key: "error",
        value: globalAgg.jobsError,
      },
    ].filter((d) => d.value > 0),
    [globalAgg]
  );

  // -----------------------------------
  // PER-KB DETAIL (charts for selected KB)
  // -----------------------------------
  const perKbDocBreakdown = useMemo(() => {
    if (!selectedRow) return null;
    const m = selectedRow.metrics || {};
    const totalDocs = m.total_docs ?? 0;
    const docsIndexed = m.docs_indexed ?? 0;
    const docsError = m.docs_error ?? 0;
    const docsUnindexed = Math.max(totalDocs - docsIndexed - docsError, 0);

    return [
      {
        name: "Indexed",
        key: "indexed",
        value: docsIndexed,
      },
      {
        name: "Unindexed",
        key: "unindexed",
        value: docsUnindexed,
      },
      {
        name: "Error",
        key: "error",
        value: docsError,
      },
    ].filter((d) => d.value > 0);
  }, [selectedRow]);

  const perKbJobStatus = useMemo(() => {
    if (!selectedRow) return null;
    const m = selectedRow.metrics || {};

    return [
      {
        name: "Completed",
        key: "completed",
        value: m.jobs_completed ?? 0,
      },
      {
        name: "Running",
        key: "running",
        value: m.jobs_running ?? 0,
      },
      {
        name: "Queued",
        key: "queued",
        value: m.jobs_queued ?? 0,
      },
      {
        name: "Error",
        key: "error",
        value: m.jobs_error ?? 0,
      },
    ].filter((d) => d.value > 0);
  }, [selectedRow]);

  const perKbLatency = useMemo(() => {
    if (!selectedRow) return 0;
    const m = selectedRow.metrics || {};
    return m.avg_latency_ms ?? 0;
  }, [selectedRow]);

  // -----------------------------------
  // RENDER
  // -----------------------------------

  if (loading) {
    return <div>Loading RAG analytics...</div>;
  }

  return (
  

          <div className="rag-page">
      <div className="rag-header">
      <h1>RAG Analytics – Knowledge Bases</h1>
</div>
      {errMsg && <p style={{ color: "red" }}>{errMsg}</p>}

      {/* ===== GLOBAL OVERVIEW ===== */}
      <section className="rag-section">
        <h2>Overall RAG Health (All KBs)</h2>

        <div className="rag-kpi-grid">
          <div className="rag-kpi-card">
            <div className="rag-kpi-label">Total Knowledge Bases</div>
            <div className="rag-kpi-value">{globalAgg.totalKb}</div>
          </div>
          <div className="rag-kpi-card">
            <div className="rag-kpi-label">Total Documents</div>
            <div className="rag-kpi-value">{globalAgg.totalDocs}</div>
          </div>
          <div className="rag-kpi-card">
            <div className="rag-kpi-label">Total Chunks (FAISS)</div>
            <div className="rag-kpi-value">{globalAgg.totalChunks}</div>
          </div>
          <div className="rag-kpi-card">
            <div className="rag-kpi-label">Total Queries</div>
            <div className="rag-kpi-value">{globalAgg.totalQueries}</div>
          </div>
          <div className="rag-kpi-card">
            <div className="rag-kpi-label">Avg Latency (per KB)</div>
            <div className="rag-kpi-value">
              {globalAgg.avgLatency}
              <span className="rag-kpi-unit"> ms</span>
            </div>
          </div>
          <div className="rag-kpi-card">
            <div className="rag-kpi-label">Indexing Jobs (All)</div>
            <div className="rag-kpi-value">{globalAgg.jobsTotal}</div>
          </div>
        </div>
      </section>

      {/* ===== GLOBAL CHARTS: DOCS + CHUNKS ===== */}
      <section className="rag-section">
        <h2>KB Comparison – Documents & Chunks</h2>
        {kbRows.length === 0 ? (
          <p>No KBs found.</p>
        ) : (
          <div className="rag-chart-grid">
            <div className="rag-chart-card">
              <h3>Documents by KB (Indexed / Unindexed / Error)</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={kbDocBarData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" hide />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="indexed" stackId="a" name="Indexed" fill={DOC_STATUS_COLORS.indexed} />
                  <Bar dataKey="unindexed" stackId="a" name="Unindexed" fill={DOC_STATUS_COLORS.unindexed} />
                  <Bar dataKey="error" stackId="a" name="Error" fill={DOC_STATUS_COLORS.error} />
                </BarChart>
              </ResponsiveContainer>
              <small>
                Each bar = KB. Stacked by document status (chunking / embedding
                success & failures reflected in Indexed vs Error).
              </small>
            </div>

            <div className="rag-chart-card">
              <h3>Chunks per KB (FAISS)</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={kbChunkBarData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" hide />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="chunks" name="Chunks" fill="#6366f1" />
                </BarChart>
              </ResponsiveContainer>
              <small>
                Indicates how much content is available to the RAG engine for
                each KB.
              </small>
            </div>
          </div>
        )}
      </section>

      {/* ===== GLOBAL CHARTS: QUERY & JOB HEALTH ===== */}
      <section className="rag-section">
        <h2>Query & Indexing Health (All KBs)</h2>
        <div className="rag-chart-grid">
          <div className="rag-chart-card">
            <h3>Queries – Success vs Error</h3>
            {queryStatusPieData.length === 0 ? (
              <p>No query analytics yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={queryStatusPieData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={3}
                    label
                  >
                    {queryStatusPieData.map((d, i) => (
                      <Cell
                        key={i}
                        fill={
                          QUERY_STATUS_COLORS[d.key] ||
                          (d.key === "success" ? "#22c55e" : "#ef4444")
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
            <small>
              Aggregated across all KBs. Errors often indicate chunking /
              embedding / LLM issues for some KBs.
            </small>
          </div>

          <div className="rag-chart-card">
            <h3>Indexing Jobs – Status Breakdown</h3>
            {jobStatusPieData.length === 0 ? (
              <p>No jobs yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={jobStatusPieData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={3}
                    label
                  >
                    {jobStatusPieData.map((d, i) => (
                      <Cell
                        key={i}
                        fill={
                          JOB_STATUS_COLORS[d.key] ||
                          (d.key === "completed" ? "#22c55e" : "#e5e7eb")
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
            <small>
              Shows chunking / embedding job health across all KBs (completed vs
              running / queued / error).
            </small>
          </div>
        </div>
      </section>

      {/* ===== PER-KB DETAIL VIEW ===== */}
      <section className="rag-section">
        <h2>Per-KB Detail – Chunking & Embedding Health</h2>

        {kbRows.length === 0 ? (
          <p>No KBs found.</p>
        ) : (
          <>
            <div className="rag-grid" style={{ marginBottom: "1rem" }}>
              <div>
                <label>Select KB</label>
                <select
                  value={selectedId}
                  onChange={(e) => setSelectedId(e.target.value)}
                >
                  {kbRows.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.id} – {r.name}
                      {r.project_id ? ` (Project ${r.project_id})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              {selectedRow && (
                <div>
                  <label>LLM / Embedding</label>
                  <div>
                    <code>{selectedRow.embedding_model}</code>
                    <br />
                    <small>
                      LLM:{" "}
                      {selectedRow.indexing_options?.llm_model ||
                        "llama3.1:8b"}
                    </small>
                  </div>
                </div>
              )}
            </div>

            {selectedRow && (
              <>
                {/* KPIs for selected KB */}
                <div className="rag-kpi-grid">
                  <div className="rag-kpi-card">
                    <div className="rag-kpi-label">Documents</div>
                    <div className="rag-kpi-value">
                      {selectedRow.metrics?.total_docs ?? 0}
                    </div>
                  </div>
                  <div className="rag-kpi-card">
                    <div className="rag-kpi-label">Chunks (FAISS)</div>
                    <div className="rag-kpi-value">
                      {selectedRow.metrics?.total_chunks ?? 0}
                    </div>
                  </div>
                  <div className="rag-kpi-card">
                    <div className="rag-kpi-label">Total Queries</div>
                    <div className="rag-kpi-value">
                      {selectedRow.metrics?.query_count ?? 0}
                    </div>
                  </div>
                  <div className="rag-kpi-card">
                    <div className="rag-kpi-label">Avg Latency</div>
                    <div className="rag-kpi-value">
                      {perKbLatency}
                      <span className="rag-kpi-unit"> ms</span>
                    </div>
                  </div>
                </div>

                {/* Charts for selected KB */}
                <div className="rag-chart-grid" style={{ marginTop: "1rem" }}>
                  <div className="rag-chart-card">
                    <h3>Documents – Chunking / Embedding Status</h3>
                    {perKbDocBreakdown && perKbDocBreakdown.length > 0 ? (
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                          <Pie
                            data={perKbDocBreakdown}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={60}
                            outerRadius={90}
                            paddingAngle={3}
                            label
                          >
                            {perKbDocBreakdown.map((d, i) => (
                              <Cell
                                key={i}
                                fill={
                                  DOC_STATUS_COLORS[d.key] ||
                                  "#6366f1"
                                }
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <p>No document stats.</p>
                    )}
                    <small>
                      “Indexed” → chunking + embeddings successful.
                      “Error” → parsing / chunking / embedding failed.
                    </small>
                  </div>

                  <div className="rag-chart-card">
                    <h3>Indexing Jobs – Chunking / Embedding Runs</h3>
                    {perKbJobStatus && perKbJobStatus.length > 0 ? (
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                          <Pie
                            data={perKbJobStatus}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={60}
                            outerRadius={90}
                            paddingAngle={3}
                            label
                          >
                            {perKbJobStatus.map((d, i) => (
                              <Cell
                                key={i}
                                fill={
                                  JOB_STATUS_COLORS[d.key] || "#0ea5e9"
                                }
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <p>No jobs yet for this KB.</p>
                    )}
                    <small>
                      Each job corresponds to a chunking + embedding pass.
                      “Error” → failed during parsing / embeddings / FAISS save.
                    </small>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
}
